import React, { FC, useState, Dispatch, SetStateAction, useEffect } from 'react';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';

import BackflowDeviceForm from '../../backflowJobForms/components/BackflowDeviceForm';
import BackflowSelectDropdown from '../../backflowJobForms/components/BackflowSelectDropdown';
import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';

import { backflowJobItemRequiredFields } from '../constants/requiredFields';
import validateRequiredFields from '../../../utils/validateRequiredFields';

import { BackflowJobItem, BackflowJobGroup } from '../../model';

import axios from '../../../sharedModules/utils/axios.utils';

interface CreateBackflowJobItemModalProps {
  backflowJobGroup: BackflowJobGroup;
  fetchBackflowJobGroup: () => void;
  fetchBackflowJobItems: () => void;
  createJobItemModalOpen: boolean;
  setCreateJobItemModalOpen: Dispatch<SetStateAction<boolean>>;
}

const CreateBackflowJobItemModal: FC<CreateBackflowJobItemModalProps> = (props) => {
  let {
    backflowJobGroup,
    fetchBackflowJobGroup,
    fetchBackflowJobItems,
    createJobItemModalOpen,
    setCreateJobItemModalOpen,
  } = props;

  let [formValues, setFormValues] = useState<BackflowJobItem>();
  let [selectedAssetId, setSelectedAssetId] = useState<string>('');
  let [errorMessage, setErrorMessage] = useState<string>('');
  // Hoping to use fieldErrors to highlight the fields with missing entries
  let [fieldErrors, setFieldErrors] = useState<any>();
  let [loading, setLoading] = useState<boolean>(false);
  let [dataFetched, setDataFetched] = useState<boolean>(false);
  let [validationErrors, setValidationErrors] = useState<string>();

  useEffect(() => {
    if (backflowJobGroup?.id) {
      setFormValues({
        ...formValues,
        backflow_job_group_id: backflowJobGroup.id,
        order_no: backflowJobGroup?.order_no,
      });
    }
  }, [backflowJobGroup]);

  const handleCloseModal = () => {
    setCreateJobItemModalOpen(false);
    setFormValues({
      backflow_job_group_id: backflowJobGroup?.id,
      order_no: backflowJobGroup?.order_no,
    });
    setSelectedAssetId('');
    setDataFetched(false);
    setErrorMessage('');
    setValidationErrors('');
    setLoading(false);
  };

  const handleCreateBackflowJobItem = async () => {
    const validate = validateRequiredFields(formValues, backflowJobItemRequiredFields);
    setValidationErrors(validate.message);
    if (!validate.isValid) return;

    const assetNo = formValues?.asset_no || '';
    if (assetNo.length > 20 || /\s/.test(assetNo)) {
      setValidationErrors('Asset # must be a single word with no spaces and 20 characters or fewer');
      return;
    }

    setLoading(true);
    try {
      await axios.post('/backflows/backflow_job_items', formValues);
      fetchBackflowJobGroup();
      fetchBackflowJobItems();
      handleCloseModal();
      setErrorMessage('');
    } catch (error) {
      setErrorMessage('Something went wrong creating a new Backflow Job');
    } finally {
      setLoading(false);
    }
  };

  const fetchAsset = async (id) => {
    setLoading(true);
    try {
      let response: any = await axios.get(`servman_systems/${id}`);
      setFormValues({
        backflow_job_group_id: backflowJobGroup.id,
        model_no: response.data.result.model_no,
        serial_no: response.data.result.serial_no,
        bfp_manufacturer_id: response.data.result.manufacturer_id,
        wm_manufacturer_id: response.data.result.wm_manufacturer_id,
        system_no: response.data.result.system_no,
        backflow_type: response.data.result.backflow_type,
        asset_no: response.data.result.asset_no,
        location: response.data.result.location,
      });
      setErrorMessage('');
    } catch (error) {
      setErrorMessage('Something went wrong fetching the Asset data');
    } finally {
      setLoading(false);
    }
  };

  const setFormToBlank = () => {
    setFormValues({
      backflow_job_group_id: backflowJobGroup.id,
      model_no: '',
      serial_no: '',
      bfp_manufacturer_id: '',
      system_no: '',
      backflow_type: '',
      asset_no: '',
      location: '',
      wm_manufacturer_id: '',
    });
    setErrorMessage('');
    setValidationErrors('');
  };

  const handleAssetChange = (value) => {
    setSelectedAssetId(value);
    if (value === '0') {
      setFormToBlank();
    } else {
      fetchAsset(value);
    }
    setDataFetched(true);
  };

  return (
    <Dialog
      open={createJobItemModalOpen}
      onClose={handleCloseModal}
      aria-labelledby="review-dialog-title"
      aria-describedby="review-dialog-description"
      fullWidth
    >
      <DialogTitle id="review-dialog-title">Add Asset to Backflow Test</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12} style={{ marginTop: 10 }}>
            <BackflowSelectDropdown
              value={selectedAssetId}
              label="Asset"
              onChange={(e) => handleAssetChange(e)}
              options={backflowJobGroup?.asset_options}
            />
          </Grid>
          {dataFetched && (
            <Grid item xs={12}>
              <BackflowDeviceForm formValues={formValues} setFormValues={setFormValues} />
            </Grid>
          )}
        </Grid>
        <Grid item xs={12}>
          <ErrorMessage message={errorMessage} />
          <ErrorMessage message={validationErrors} />
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button color="secondary" variant="contained" onClick={handleCloseModal}>
          Cancel
        </Button>
        <Button
          color="primary"
          variant="contained"
          disabled={loading || !dataFetched}
          onClick={handleCreateBackflowJobItem}
        >
          Submit Job Item
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateBackflowJobItemModal;
